import { useState, useEffect, useCallback, createContext, useContext, memo, useMemo, useRef } from "react";
import {
    Button,
    ButtonBase, createTheme, Stack,
    LinearProgress, Box, Grid,
    Radio, RadioGroup, FormControlLabel,
    Paper,
    Typography,
    TableContainer, Tabs, Tab, buttonBaseClasses
} from "@mui/material";
import { green, orange, red, blue, purple } from "@mui/material/colors";
import GridOnRoundedIcon from "@mui/icons-material/GridOnRounded";
import TimelineRoundedIcon from "@mui/icons-material/TimelineRounded";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import CalculateIcon from '@mui/icons-material/Calculate';
import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined';
import TodayIcon from '@mui/icons-material/Today';
import "../InventoryDetail.css";
import { ConditionalWrapper } from "../../../../Components/utils/ConditionalWrapper";
import { ThemeProvider } from "@mui/material/styles";
import ExitSimulationModal from "../../../../Components/DetailsPageLowerLayer/ExitSimulationModal";
import { inventoryAlternateLocAndItemsCompare } from "../../../../services/Inventory/InventoryDetailServices";
import { AlternateLocationRenderTable } from "./AlternateLocationRenderTable";
import { AlternateLocationRenderChart } from "./AlternateLocationRenderChart";

const theme = createTheme({
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    backgroundColor: "#2b0920ff",
                    color: "#ffffff",
                    borderRadius: "5px",
                    paddingTop: "2px",
                    paddingBottom: "2px",
                    paddingLeft: "8px",
                    paddingRight: "8px",
                    fontSize: "13px",
                    textTransform: "none",
                    height: '28px',
                    "&:hover": {
                        backgroundColor: "#614b5aff"
                    }
                }
            }
        }
    }
});

const tabMap = new Map([
    [0, 'Hybrid'],
    [1, 'Weekly'],
    [2, 'Monthly'],
]);

const timeframeValues = ["Q1FY26", "Q2FY26", "Q3FY26", "Q4FY26", "Q1FY27"];

export function AlternateLocationGraphView({ inventoryId, siteCode, siteCodes = [], inventoryIds = [], onExitCompare, label }) {
    const [isLoading, setIsLoading] = useState({ data: true, error: false });
    const [isCalculating, setIsCalculating] = useState(false);
    const [columns, setColumns] = useState([]);
    const [activeTab, setActiveTab] = useState(0);

    const updateActiveTab = (tab) => {
        setActiveTab(tab);
    }
    const [isActive, setIsActive] = useState({
        table: false,
        chart: false,
        hybrid: true
    });
    const [currentView, setCurrentView] = useState({
        compare: true,
        simulate: false,
    });
    const [rows, setRows] = useState({});
    // to use this in simulation/view for initialization
    const [editableRows, setEditableRows] = useState({});
    const [showExitModal, setShowExitModal] = useState(false);
    const [timeframe, setTimeframe] = useState("Q1FY26");       // default API value
    const [showTimeOptions, setShowTimeOptions] = useState(false);
    const [editedSites, setEditedSites] = useState(() => new Set());
    const [editedColumnIndex, setEditedColumnIndex] = useState({});
    const ref = useRef(null);

    useEffect(() => {
        setIsLoading({ data: false });
        setEditableRows({});
        setColumns(['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX']);
    }, [activeTab, timeframe]);

    useEffect(() => {
        let isCurrent = true;                                   // flag for stale calls

        const fetchData = async () => {
            const apiResponse = await inventoryAlternateLocAndItemsCompare(inventoryId, siteCode, label,
                tabMap.get(activeTab).toUpperCase(), timeframe, siteCodes, inventoryIds);
            if (!isCurrent) return;                             // Skip stale result

            // On API error
            if (apiResponse.error) {
                setIsLoading({ data: false, error: true });     // table hidden
                return;
            }
            setRows(apiResponse.measures);
            // Set editable rows to baseline initially
            setEditableRows(JSON.parse(JSON.stringify(apiResponse.measures)));
            if (apiResponse?.headers?.length) {
                const mapped = apiResponse.headers.map(h => {
                    const display = h.displayDate || h.displaydate || "";
                    const fiscal = h.fiscalLabel || h.fiscallabel || "";
                    return display.includes("-") ? `${display.split('-')[0]}-\n${display.split('-')[1]} ${fiscal}` : `${display}\n ${fiscal}`;
                });
                setColumns(mapped);
            }
            setIsLoading({ data: true, error: false });
            return;
        };
        fetchData();
        return () => {
            isCurrent = false;                                 // Cleanup → in-flight request becomes stale
        };
    }, [activeTab, inventoryId, siteCode, timeframe, label, siteCodes.join(","), inventoryIds.join(",")]);

    const startCalculation = () => {
        setIsCalculating(true);

        setTimeout(() => {
            setEditableRows(prevRows => {
                const updatedRows = structuredClone(prevRows);
                // Loop through each CPS/site group
                Object.entries(updatedRows).forEach(([siteKey, siteData]) => {
                    if (!editedSites.has(siteKey)) return;
                    // Find the relevant measures
                    const receipts = siteData.measures.find(m => m.name === "Total Supply");
                    const demands = siteData.measures.find(m => m.name === "Total Demand");
                    const projected = siteData.measures.find(m => m.name === "Projected Inventory");

                    if (!receipts || !demands || !projected) return;

                    const startIdx = editedColumnIndex[siteKey] ?? projected.quantities.length;
                    const newProjection = projected.quantities.map(q => ({ ...q }));
                    let prevValue = startIdx === 0 ? siteData.onHand?.unrestrictedStock ?? 0
                        : projected.quantities[startIdx - 1]?.quantity ?? 0;

                    for (let i = startIdx; i < newProjection.length; i++) {
                        const rcpt = Number(receipts.quantities[i]?.quantity ?? 0);
                        const req = Number(demands.quantities[i]?.quantity ?? 0);
                        prevValue = prevValue + rcpt - req;
                        newProjection[i].quantity = prevValue;
                    }
                    projected.quantities = newProjection;
                });
                return updatedRows;
            });
            setIsCalculating(false);
        }, 600);
    };

    const handleValueChange = useCallback((siteKey, measureIndex, colIndex, value) => {
        setEditedSites(prev => {
            const next = new Set(prev);
            next.add(siteKey);              // track ALL edited plants
            return next;
        });
        setEditedColumnIndex(prev => ({
            ...prev,
            [siteKey]: Math.min(prev[siteKey] ?? colIndex, colIndex)
        }));
        setEditableRows(prev => {
            const updated = structuredClone(prev);
            const measure = updated[siteKey].measures[measureIndex];

            if (typeof measure.quantities[colIndex] === "object") {
                measure.quantities[colIndex].quantity = Number(value);
            } else {
                measure.quantities[colIndex] = Number(value);
            }
            measure.isEdited = true;
            return updated;
        });
    }, []);

    // Generate the ySeriesData dynamically from response.measures
    const colors = useMemo(() => [orange[500], green[500], red[500], blue['A700'], purple['A700']], []);

    const ySeriesData = useMemo(() => {
        return Object.entries(editableRows).map(([siteKey, siteData], idx) => {
            const projectedInventory = siteData.measures.find(m => m.name === "Projected Inventory");
            const minStock = siteData.measures.find(m => m.name === "Min Stock");
            const maxStock = siteData.measures.find(m => m.name === "Max Stock");
            return {
                label: siteKey, // or siteData.plantDetails.plantName
                data: projectedInventory.quantities.map(q => q.quantity),
                minStock: minStock.quantities.map(q => q.quantity),
                maxStock: maxStock.quantities.map(q => q.quantity),
                id: `series-${idx}`, // must be unique
                color: colors[idx % colors.length],
                area: false,        // no shaded area
                showMark: true,
                connectNulls: true
            };
        });
    }, [editableRows, colors]);

    const onTimeframeChange = (value) => {
        setTimeframe(value);
        setShowTimeOptions(false);
    }

    const TimeframeOptions = () => {
        return (
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2, position: "sticky", left: 0 }}>
                <Paper
                    elevation={3}
                    sx={{
                        paddingLeft: 2, paddingBottom: 0.5,
                        backgroundColor: "#fafafa",
                        border: "1px solid #ccc",
                        borderRadius: "8px"
                    }}
                >
                    <Typography
                        variant="subtitle1"
                        sx={{ mb: 0.7, fontWeight: "bold", color: "#6d6c6cff" }}
                    >
                        Select Timeframe
                    </Typography>

                    <RadioGroup
                        row
                        onChange={(e) => onTimeframeChange(e.target.value)}
                        sx={{ flexDirection: "row", flexWrap: "nowrap" }}
                    >
                        {timeframeValues.map((opt) => (
                            <FormControlLabel
                                checked={timeframe === opt}
                                key={opt}
                                value={opt}
                                control={
                                    <Radio
                                        sx={{
                                            "& .MuiSvgIcon-root": { fontSize: "1.2rem" },
                                            p: 0,
                                            color: "#969393ff",
                                            "&.Mui-checked": { color: "#000" }
                                        }}
                                    />
                                }
                                label={opt}
                                sx={{
                                    "& .MuiFormControlLabel-label": {
                                        fontSize: "0.8rem",
                                        color: "#000",
                                        marginLeft: 0.2
                                    },
                                    marginRight: 2.5,
                                }}
                            />
                        ))}
                    </RadioGroup>
                </Paper>
            </Box>
        )
    }
    return (
        <div style={{ width: '100%' }} ref={ref}>
            <ThemeProvider theme={theme}>
                <Grid container sx={{ marginBottom: '10px', position: "sticky", left: 0, alignItems: 'center', mr: 2 }}>
                    <Grid size={{ xs: 4 }}>
                        <Stack direction="row" gap={1}>
                            <ConditionalWrapper condition={currentView.compare}>
                                <Button onClick={() => {
                                    setCurrentView({ simulate: true });
                                    setEditableRows(JSON.parse(JSON.stringify(rows)));
                                }}>
                                    Simulate
                                    <ViewInArIcon sx={{ marginLeft: 1 }} />
                                </Button>
                                <Button
                                    onClick={() => {
                                        setCurrentView({ compare: false, simulate: false });
                                        if (onExitCompare) onExitCompare(); // notify parent to show site table
                                    }}>
                                    <ClearOutlinedIcon sx={{ marginLeft: 0.5 }} />
                                </Button>
                            </ConditionalWrapper>
                            <ConditionalWrapper condition={currentView.simulate}>
                                <Button sx={{
                                    "&.Mui-disabled": {
                                        backgroundColor: "#c4c0c0ff",
                                        color: "#000000"
                                    }
                                }}
                                    onClick={() => {
                                        startCalculation();
                                    }}
                                    disabled={!isLoading.data || isCalculating}>
                                    Calculate
                                    <CalculateIcon sx={{ marginLeft: 1 }} />
                                </Button>
                                <Button
                                    onClick={() => {
                                        setShowExitModal(true);
                                    }}>
                                    <ClearOutlinedIcon />
                                </Button>
                            </ConditionalWrapper>
                        </Stack>
                    </Grid>
                    <Grid size={{ xs: 4 }}>
                        <Stack sx={{ justifySelf: 'center', width: '100%' }}>
                            <Tabs
                                value={activeTab}
                                onChange={(e, tab) => updateActiveTab(tab)}
                                sx={{
                                    backgroundColor: 'black',
                                    color: 'white',
                                    fontWeight: 'bold',
                                    borderRadius: 3,
                                    minHeight: '30px',
                                    [`.${buttonBaseClasses.root}`]: {
                                        minHeight: '30px',
                                        lineHeight: '1',
                                        // borderRight: '1px solid #e0e0e0 '
                                    }
                                }}
                                slotProps={{
                                    indicator: {
                                        sx: {
                                            backgroundColor: 'transparent'
                                        }
                                    },
                                    list: {
                                        sx: {
                                            justifyContent: 'center',
                                        }
                                    },
                                }}
                                variant='fullWidth'
                                textColor="inherit"
                                aria-label="chart-data-tab"
                            >
                                <Tab label="Hybrid" />
                                <Tab label="Weekly" />
                                <Tab label="Monthly" />
                            </Tabs>
                        </Stack>
                    </Grid>
                    <Grid container size={{ xs: 4 }} justifyContent='flex-end'>
                        <Stack direction="row-reverse" columnGap={2}>
                            <ButtonBase
                                onClick={() =>
                                    setIsActive({ table: true })
                                }
                            >
                                <GridOnRoundedIcon color={isActive.table ? "primary" : ""} />
                            </ButtonBase>
                            <ButtonBase
                                onClick={() =>
                                    setIsActive({ chart: true })
                                }
                            >
                                <TimelineRoundedIcon color={isActive.chart ? "primary" : ""} />
                            </ButtonBase>
                            <ButtonBase
                                onClick={() =>
                                    setIsActive({ hybrid: true })
                                }
                            >
                                <QueryStatsIcon color={isActive.hybrid ? "primary" : ""} />
                            </ButtonBase>
                            <ButtonBase
                                size="large"
                                onClick={() => setShowTimeOptions(!showTimeOptions)}
                            // sx={{
                            //   borderColor: "#808080",
                            //   color: "#000",
                            //   backgroundColor: "#f5f5f5",
                            //   "&:hover": { backgroundColor: "#d3d3d3" }
                            // }}
                            >
                                <TodayIcon color={showTimeOptions ? 'primary' : ''} />
                            </ButtonBase>
                        </Stack>
                    </Grid>
                </Grid>
                <ConditionalWrapper condition={showTimeOptions}>
                    <TimeframeOptions />
                </ConditionalWrapper>
            </ThemeProvider>

            {/* Show linear progress bar at top when calculating */}
            <ConditionalWrapper condition={isCalculating}>
                <Stack sx={{
                    width: '100%', my: 5,
                    "& .MuiLinearProgress-bar": {
                        backgroundColor: "#545454ff"  // grey
                    },
                }} spacing={2}>
                    <LinearProgress />
                    <LinearProgress />
                    <LinearProgress />
                </Stack>
            </ConditionalWrapper>

            <HighlightMarkProvider>
                <TableContainer sx={{ overflowX: 'auto', width: '100%' }}>
                    <ConditionalWrapper condition={!isCalculating && (isActive.hybrid || isActive.chart)}>
                        <AlternateLocationRenderChart
                            isLoading={isLoading}
                            xSeries={columns}
                            ySeries={ySeriesData}
                            height={isActive.chart ? 600 : 300}
                            width={ref.current ? ref.current.clientWidth : 0}
                        />
                    </ConditionalWrapper>

                    <ConditionalWrapper condition={!isCalculating && isLoading.data && (isActive.table || isActive.hybrid)}>
                        <AlternateLocationRenderTable
                            columns={columns}
                            rows={rows}               // base rows (normal or normal snapshot)
                            editedRows={editableRows}
                            currentView={currentView}
                            handleValueChange={handleValueChange}
                            label={label}
                        />
                    </ConditionalWrapper>
                </TableContainer>
                <ExitSimulationModal
                    open={showExitModal}
                    handleClose={() => setShowExitModal(false)}
                    onConfirm={() => {
                        setCurrentView({ compare: true, simulate: false });
                        setEditableRows(JSON.parse(JSON.stringify(rows)));
                        setShowExitModal(false);
                    }}
                    theme={theme}
                />
            </HighlightMarkProvider>
        </div >
    );
}

const HighlightMarkContext = createContext({
    highlightMMarkIdx: -1,
    setHighlightMarkIdx: () => { }
})

export const useHighlightMarkIdx = () => useContext(HighlightMarkContext)

const HighlightMarkProvider = memo(({ children }) => {
    const [highlightMarkIdx, setHighlightMarkIdx] = useState(-1)
    const updateHighlightMarkIdx = useCallback((idx) => setHighlightMarkIdx(idx), [])
    const contextValue = useMemo(() => ({ highlightMarkIdx, setHighlightMarkIdx: updateHighlightMarkIdx }), [highlightMarkIdx, updateHighlightMarkIdx]);

    return (
        <HighlightMarkContext.Provider value={contextValue}>
            {children}
        </HighlightMarkContext.Provider>
    )
})